import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import Button from "src/components/button";
import Slider from "src/components/slider";
import EditBanner from "./editBanner";
import WarningMessage from "src/components/warningMessage";

import {
  invalidateShareSettingsUpdate,
  updateShareTemplate,
} from "src/redux/actions/shareTemplates";

import { ContentType } from "src/types/common";
import { TemplateType } from "src/types/shareTemplate";
import { IShareUrl } from "../linkedInPost";

import _ from "lodash";
import cx from "classnames";
import styles from "./styles.module.scss";
import DescriptionEditor from "../linkedInPost/editor";
import CollectionWidgetManager from "src/services/collections.widget.service";

const LinkedInLogo = "/images/linkedIn.svg";
interface IShareSettingsProps {
  onCancel: () => void;
  entity: any;
  linkedInShareUrl: IShareUrl;
}

interface IPlatform {
  description: string;
  banner: any;
}

const ShareSettings: React.FC<IShareSettingsProps> = ({
  linkedInShareUrl,
  onCancel,
  entity,
}) => {
  const dispatch = useDispatch();
  const shareTemplates = useSelector((state: any) => state.shareTemplates);
  const shareTemplate = shareTemplates[entity.id];
  const shareTemplateData = shareTemplate?.item;

  const [linkedInData, setLinkedInData] = React.useState<IPlatform>({
    description: shareTemplateData?.message || "",
    banner: {},
  });
  const [emptyDescriptionError, setEmptyDescriptionError] =
    React.useState<boolean>(false);
  const [activePlatform, setActivePlatform] = React.useState<TemplateType>();
  const [showEditBanner, setShowEditBanner] = React.useState<boolean>(false);
  const [isWordLimitExceeded, setIsWordLimitExceeded] =
    React.useState<boolean>(false);

  const isUpdating = shareTemplate?.updation?.loading;
  const isUpdated = shareTemplate?.updation?.succeeded;

  useEffect(() => {
    setLinkedInData({
      description: shareTemplateData?.message || "",
      banner: shareTemplateData?.mediaDTO || {},
    });
  }, [shareTemplateData]);

  useEffect(() => {
    if (isUpdated && !isUpdating) {
      onCancel();
      dispatch(
        invalidateShareSettingsUpdate({
          entityId: entity.id,
          entityType: ContentType.Asset,
        }),
      );
    }
  }, [isUpdated, isUpdating]);

  const handleContentChange = (platform: TemplateType, description: string) => {
    switch (platform) {
      case TemplateType.LinkedIn:
        setLinkedInData({
          ...linkedInData,
          description: description,
        });
        break;
    }
  };

  const getActivePlatformBanner = useMemo(() => {
    switch (activePlatform) {
      case TemplateType.LinkedIn:
        return linkedInData.banner;
      default:
        return "";
    }
  }, [activePlatform, linkedInData]);

  const updateBanner = (banner: any) => {
    switch (activePlatform) {
      case TemplateType.LinkedIn:
        setLinkedInData({
          ...linkedInData,
          banner: banner,
        });
        break;
    }
  };

  const handleBannerClick = (platform: TemplateType) => {
    setActivePlatform(platform);
    setShowEditBanner(true);
  };

  const handleUpdate = () => {
    if (_.size(linkedInData.description) === 0) {
      setEmptyDescriptionError(true);
      setTimeout(() => {
        setEmptyDescriptionError(false);
      }, 3000);
      return;
    }

    dispatch(
      updateShareTemplate({
        entityId: entity.id,
        entityType: ContentType.Asset,
        templateType: TemplateType.LinkedIn,
        requestBody: {
          message: linkedInData.description,
          media: _.get(linkedInData, "banner.id", ""),
          type: TemplateType.LinkedIn,
        },
      }),
    );
  };

  return (
    <div className={styles.shareSettings}>
      <p className={styles.title}>share settings</p>
      <Platform
        logo={LinkedInLogo}
        data={linkedInData}
        onChange={(description) =>
          handleContentChange(TemplateType.LinkedIn, description)
        }
        handleBannerClick={() => handleBannerClick(TemplateType.LinkedIn)}
        setLimitReached={setIsWordLimitExceeded}
      />

      <div className={styles.footer}>
        {(isWordLimitExceeded || emptyDescriptionError) && (
          <div className={styles.warningMessage}>
            <WarningMessage
              type="ERROR"
              header={
                isWordLimitExceeded
                  ? "You've exceeded the character limit"
                  : "Add a description to share your post"
              }
              message=""
            />
          </div>
        )}

        <div className={styles.buttonContainer}>
          <Button
            variant="primary"
            className={styles.saveButton}
            icon="paperflite-tick"
            text={isUpdating ? "Updating..." : "Update"}
            disabled={
              showEditBanner ||
              isUpdating ||
              emptyDescriptionError ||
              isWordLimitExceeded
            }
            onClick={handleUpdate}
          />
          <Button
            variant="secondary"
            className={styles.cancelButton}
            onClick={() => {
              onCancel();
            }}
            text="Cancel"
          />
        </div>
      </div>

      <Slider.Dialog
        width={"380px"}
        show={showEditBanner}
        onClose={() => setShowEditBanner(false)}
      >
        {showEditBanner && (
          <EditBanner
            show={showEditBanner}
            onClose={() => setShowEditBanner(false)}
            setBanner={updateBanner}
            banner={getActivePlatformBanner}
          />
        )}
      </Slider.Dialog>
    </div>
  );
};

interface IPlatformProps {
  data: IPlatform;
  logo: string;
  onChange: (e: any) => void;
  handleBannerClick: () => void;
  setLimitReached: (value: boolean) => void;
}

const Platform: React.FC<IPlatformProps> = ({
  data,
  logo,
  onChange,
  handleBannerClick,
  setLimitReached,
}) => {
  const banner = data?.banner;
  const description = data?.description;
  const widgetManagerRef = React.useRef<CollectionWidgetManager>(
    new CollectionWidgetManager(),
  );
  return (
    <div className={styles.platformContainer}>
      <img className={styles.image} src={logo} />
      <br />
      <div className={styles.content}>
        <div className={styles.descriptionContainer}>
          <DescriptionEditor
            content={description}
            onChange={(value: string) => onChange(value)}
            className={styles.description}
            placeholder="What do you want to share today?"
            widgetManager={widgetManagerRef.current}
            setLimitReached={setLimitReached}
          />
          <div className={styles.characterLimit}>*character limit: 3000</div>
        </div>

        <div className={styles.banner} onClick={handleBannerClick}>
          {banner?.full ? (
            <div className={styles.bannerImage}>
              <i className="paperflite-edit" />
              <img src={banner.full} />
            </div>
          ) : (
            <div className={styles.uploadBanner}>
              <i className={cx("paperflite-upload")} />
              <span>upload artwork</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ShareSettings;
